<template>
  <a-drawer
    class="calculation-drawer"
    placement="right"
    :closable="true"
    :width="720"
    :visible="visible"
    @close="onClose"
  >
    <template #title>
      <span class="calculation-drawer__title">Cashback Calculation</span>
    </template>

    <div class="calculation-drawer__content">
      <div class="calculation-drawer__block">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <a-icon class="d-flex font-size-18 mr-2 mb-1" type="calendar" />
            <span class="calculation-drawer__text-title mb-0">Activity period</span>
            <a-tooltip placement="topLeft" arrow-point-at-center>
              <template slot="title">
                <p>Cashback is calculated starting from the date the user became active.</p>
                <span>The end date will be set when the activity period is completed.</span>
              </template>
              <a-icon class="d-flex ml-2" type="info-circle" />
            </a-tooltip>
          </div>
          <span class="calculation-drawer__text">{{ activityPeriod }}</span>
        </div>
      </div>

      <div class="calculation-drawer__block">
        <span class="calculation-drawer__subtitle">Formula</span>
        <div class="d-flex justify-content-center align-items-cenger my-2 font-size-18">
          <div class="formula">
            <span class="op">min</span>
            <span class="bracket">(</span>

            <span class="bracket">(</span>
            <span class="num blue">{{ cashbackInfo.bets }}</span>
            <span class="op">−</span>
            <span class="num red">{{ cashbackInfo.payouts }}</span>
            <span class="bracket">)</span>

            <span class="op">×</span>
            <span class="num violet">{{ cashbackInfo.cashback.percent === 'Percent' ? cashbackInfo.cashback.percent : `${cashbackInfo.cashback.percent} %` }}</span>

            <span class="comma">,</span>
            <span class="num green">{{ cashbackInfo.cashback.limit }}</span>

            <span class="bracket">)</span>

            <span class="equals">=</span>
            <span class="result text-dark">{{ cashbackInfo.result }}</span>
          </div>
        </div>
      </div>

      <div class="calculation-drawer__cards">
        <div class="calculation-drawer__block" style="flex: 1 1 auto;">
          <div class="d-flex flex-column">
            <div class="d-flex justify-content-between align-items-center w-full mb-2">
              <div class="d-flex align-items-center">
                <div class="calculation-drawer__circle calculation-drawer__circle-blue mr-2" />
                <span class="calculation-drawer__text-title">{{ isAffiliate ? 'Referrals` bets' : 'Bets'}}</span>
              </div>
              <a-tooltip placement="topLeft" arrow-point-at-center>
                <template slot="title">
                  <span v-if="isAffiliate">Total bets placed by referrals during the activity period. Free bets excluded</span>
                  <span v-else>Total bets placed during the activity period. Free bets excluded.</span>
                </template>
                <a-icon class="d-flex ml-2" type="info-circle" />
              </a-tooltip>
            </div>
            <span class="font-size-24 font-bold num blue">{{ cashbackInfo.bets.toLocaleString('en-US') }} ₽</span>
          </div>
        </div>
        <div class="calculation-drawer__block" style="flex: 1 1 auto;">
          <div class="d-flex flex-column">
            <div class="d-flex justify-content-between align-items-center w-full mb-2">
              <div class="d-flex align-items-center">
                <div class="calculation-drawer__circle calculation-drawer__circle-red mr-2" />
                <span class="calculation-drawer__text-title">{{ isAffiliate ? 'Referrals` payouts' : 'Payouts'}}</span>
              </div>
              <a-tooltip placement="topLeft" arrow-point-at-center>
                <template slot="title">
                  <span v-if="isAffiliate">Total payouts credited to referrals during this period</span>
                  <span v-else>Total payouts credited during this period, outside of this referral program</span>
                </template>
                <a-icon class="d-flex ml-2" type="info-circle" />
              </a-tooltip>
            </div>
            <span class="font-size-24 font-bold num red">{{ cashbackInfo.payouts.toLocaleString('en-US') }} ₽</span>
          </div>
        </div>
        <div class="calculation-drawer__block" style="flex: 1 1 auto;">
          <div class="d-flex flex-column">
            <div class="d-flex justify-content-between align-items-center w-full mb-2">
              <div class="d-flex align-items-center">
                <div class="calculation-drawer__circle calculation-drawer__circle-violet mr-2" />
                <span class="calculation-drawer__text-title">Cashback %</span>
              </div>
              <a-tooltip placement="topLeft" arrow-point-at-center>
                <template slot="title">
                  <span>Percentage defined in the referral program settings for this role</span>
                </template>
                <a-icon class="d-flex ml-2" type="info-circle" />
              </a-tooltip>
            </div>
            <span class="font-size-24 font-bold num violet">{{ cashbackInfo.cashback.percent }} %</span>
          </div>
        </div>
        <div class="calculation-drawer__block" style="flex: 1 1 auto;">
          <div class="d-flex flex-column">
            <div class="d-flex justify-content-between align-items-center w-full mb-2">
              <div class="d-flex align-items-center">
                <div class="calculation-drawer__circle calculation-drawer__circle-green mr-2" />
                <span class="calculation-drawer__text-title">Cashback limit</span>
              </div>
              <a-tooltip placement="topLeft" arrow-point-at-center>
                <template slot="title">
                  <span>Cashback limit defined in the referral program settings for this role</span>
                </template>
                <a-icon class="d-flex ml-2" type="info-circle" />
              </a-tooltip>
            </div>
            <span class="font-size-24 font-bold num green">{{ cashbackInfo.cashback.limit.toLocaleString('en-US') }} ₽</span>
          </div>
        </div>
      </div>

      <div class="calculation-drawer__block">
        <span class="calculation-drawer__subtitle">Calculation steps</span>
        <div class="d-flex flex-column" style="gap: 16px;">
          <div
            v-for="(step, index) in calculationSteps"
            :key="index"
            class="calculation-drawer__step"
          >
            <div class="calculation-drawer__step__number">
              {{ index + 1 }}
            </div>
            <div>
              <span class="calculation-drawer__step__title">{{ step.title }}</span>
              <div v-html="step.content" />
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex flex-column">
        <div
          class="calculation-drawer__block text-white"
          style="
            background-color: #323232;
            padding-left: 34px;
            padding-right: 34px;
          "
        >
          <div class="d-flex justify-content-between align-items-center w-full">
            <div class="d-flex flex-column">
              <span class="uppercase">Total cashback</span>
              <span class="font-size-24">{{ cashbackInfo.result === 'Cashback' ? '—' : `${cashbackInfo.result} ₽` }}</span>
            </div>
            <span v-if="limitApplied" class="uppercase">Limit applied</span>
          </div>
        </div>

        <div class="d-flex justify-content-center mt-2">
          <p v-html="descriptionText" />
        </div>
      </div>
    </div>
  </a-drawer>
</template>

<script>

export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    info: {
      type: [Object, null],
      default: null,
    },
    memberStatus: {
      type: String,
    },
    isAffiliate: {
      type: Boolean,
    },
  },
  data: () => ({
    cashbackInfo: {
      bets: 'Bets',
      cashback: {
        limit: 'Limit',
        percent: 'Percent',
      },
      free_bets: 'FreeBets',
      payouts: 'Payouts',
      period: [],
      result: 'Cashback',
      program: {
        status: 'inactive',
      },
    },
  }),
  watch: {
    visible(val) {
      if (!val) {
        this.clearCashbackInfo()
        return
      }

      this.cashbackInfo = { ...this.cashbackInfo, ...this.info }
    },
  },
  computed: {
    isInactiveProgram() {
      return this.info?.program?.status === 'inactive'
    },
    isActiveMember() {
      return this.memberStatus === 'active'
    },
    activityPeriod() {
      const period = this.cashbackInfo.period
      if (!period?.length || this.isInactiveProgram) {
        return '—'
      }

      return `${!period[0] ? '' : this.formatDate(period[0])} — ${period[1] !== null ? this.formatDate(period[1]) : this.isActiveMember ? 'Ongoing' : ''}`
    },
    netResult() {
      return this.cashbackInfo.bets - this.cashbackInfo.payouts
    },
    applyCashback() {
      return (this.netResult * this.cashbackInfo.cashback.percent) / 100
    },
    applyLimit() {
      return Math.min(this.applyCashback, this.cashbackInfo.cashback.limit)
    },
    limitApplied() {
      return this.applyCashback > this.cashbackInfo.cashback.limit
    },
    calculationSteps() {
      return [
        {
          title: 'Net result',
          content: `${this.cashbackInfo.bets.toLocaleString('en-US')} − ${this.cashbackInfo.payouts.toLocaleString('en-US')}  = ${this.netResult.toLocaleString('en-US')}`,
        },
        {
          title: 'Apply cashback %',
          content: `${this.netResult.toLocaleString('en-US')} × ${this.cashbackInfo.cashback.percent}% = ${this.applyCashback.toLocaleString('en-US')}`,
        },
        {
          title: 'Apply cashback limit',
          content: `min(${this.applyCashback.toLocaleString('en-US')}, ${this.cashbackInfo.cashback.limit.toLocaleString('en-US')}) = <span class="text-black uppercase">${this.applyLimit.toLocaleString('en-US')}</span>`,
        },
      ]
    },
    descriptionText() {
      if (this.isInactiveProgram) {
        return this.isAffiliate
          ? 'Cashback will appear once his referrals become active participants and start placing bets'
          : 'Cashback will appear once the user becomes active and starts placing bets'
      }
      return 'Cashback is credited only when the result is positive. <br>Values will appear once the user starts placing bets.'
    },
  },
  methods: {
    formatDate(date) {
      return this.$moment(date).tz(this.$moment.tz.guess()).format('DD.MM.YYYY')
    },
    clearCashbackInfo() {
      this.cashbackInfo = {
        bets: 'Bets',
        cashback: {
          limit: 'Limit',
          percent: 'Percent',
        },
        free_bets: 'FreeBets',
        payouts: 'Payouts',
        period: [],
        result: 'Cashback',
        program: {
          status: 'inactive',
        },
      }
    },
    onClose() {
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss">
.calculation-drawer {
  position: relative;
  .ant-drawer-header {
    position: sticky;
    top: 0;
  }
  .ant-drawer-body {
    height: calc(100% - 57px);
    background-color: #dadbe036;
  }
  &__title {
    font-size: 24px;
    font-weight: bold;
  }
  &__content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  &__block {
    padding: 16px;
    border-radius: 8px;
    background-color: white;
    -webkit-box-shadow: 2px 2px 8px 0px rgba(34, 60, 80, 0.2);
    -moz-box-shadow: 2px 2px 8px 0px rgba(34, 60, 80, 0.2);
    box-shadow: 2px 2px 8px 0px rgba(34, 60, 80, 0.2);
  }
  &__subtitle {
    display: block;
    font-size: 18px;
    font-weight: 500;
    color: #323232;
    text-transform: uppercase;
    margin-bottom: 8px;
  }
  &__text-title {
    font-size: 16px;
    font-weight: 500;
    color: #323232;
  }
  &__text {
    font-size: 16px;
    color: #323232;
  }
  &__cards {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, auto);
    gap: 8px;
  }
  &__circle {
    border-radius: 100%;
    width: 10px;
    height: 10px;
    border: 2px solid red;
    background-color: rgba(255, 0, 0, .3);
    &-blue {
      border-color: #2D8CFE;
      background-color: rgba(45, 140, 254, .3);
    }
    &-red {
      border-color: #DD2928;
      background-color: rgba(221, 41, 40, .3);
    }
    &-green {
      border-color: #25B160;
      background-color: rgba(37, 177, 96, .3);
    }
    &-violet {
      border-color: #6558F5;
      background-color: rgba(101, 88, 245, .3);
    }
  }
  &__step {
    display: flex;
    gap: 10px;
    color: #434343;
    &__number {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 25px;
      height: 25px;
      color: #434343;
      background-color: rgba(200, 196, 219, .5);
      border-radius: 100%;
      font-weight: 600;
    }
    &__title {
      color: #434343;
    }
  }

  .formula {
    font-family: 'Inter', 'Roboto', sans-serif;
    font-size: 18px;
    line-height: 1.6;
    color: #434343;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
  }

  .num {
    font-weight: 600;
  }

  .num.blue {
    color: #2D8CFE;
  }

  .num.red {
    color: #DD2928;
  }

  .num.green {
    color: #25B160;
  }

  .num.violet {
    color: #6558F5;
  }

  .op,
  .bracket,
  .comma,
  .equals {
    color: #434343;
    font-weight: 500;
  }

  .percent {
    color: #434343;
    font-weight: 600;
  }

  .result {
    margin-left: 4px;
    font-weight: 700;
    font-size: 20px;
  }
}
</style>
